import { createContext, useEffect, useReducer, ReactNode } from "react";
import axios from "axios";
import { verify, JWT_SECRET } from "../utils/jwt";
import config from "../consts";
import { User } from "../api/typescript/UserApi";

interface AuthenticatedUser extends User {
  permissions?: unknown;
  avatar?: string;
  features_enabled?: unknown;
}

interface State {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthenticatedUser | null;
  token: string | null;
}

type AccessToken = string | null;

type LoginResult = {
  code: number;
  user_id?: number;
};

enum ActionType {
  INITIALIZE = "INITIALIZE",
  LOGIN = "LOGIN",
  LOGOUT = "LOGOUT",
  REGISTER = "REGISTER",
}

interface InitializeAction {
  type: ActionType.INITIALIZE;
  payload: {
    isAuthenticated: boolean;
    user: AuthenticatedUser | null;
    token: string | null;
  };
}

interface LoginAction {
  type: ActionType.LOGIN;
  payload: {
    user: AuthenticatedUser;
    token: string;
  };
}

interface LogoutAction {
  type: ActionType.LOGOUT;
}

interface RegisterAction {
  type: ActionType.REGISTER;
  payload: {
    user: AuthenticatedUser;
    token: string;
  };
}

type AuthAction =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction;

interface JWTContext extends State {
  platform: string;
  login: (email: string, password: string) => Promise<LoginResult | void>;
  loginWithMicrosoft: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  twoFactorLogin: (token: string, user: AuthenticatedUser) => Promise<void>;
  register: (email: string, name: string, password: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const ACCESS_TOKEN_STORAGE_KEY = "accessToken";
const LEGACY_AUTH_TOKEN_STORAGE_KEY = "authToken";

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  token: null,
};

const getStoredAccessToken = (): AccessToken =>
  window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) ||
  window.localStorage.getItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);

const setSession = (accessToken: AccessToken) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    localStorage.setItem(LEGACY_AUTH_TOKEN_STORAGE_KEY, accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state: State, action: AuthAction): State => {
  switch (action.type) {
    case ActionType.INITIALIZE:
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        token: action.payload.token,
      };
    case ActionType.LOGIN:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
      };
    case ActionType.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
      };
    case ActionType.REGISTER:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
      };
    default:
      return state;
  }
};

const hydrateAuthenticatedUser = async (): Promise<AuthenticatedUser> => {
  const response = await axios.get(`${config.api}/user/profile`);
  const user = response.data as AuthenticatedUser;

  try {
    const { data: appSettings } = await axios.get(
      `${config.api}/appsettings/appsetting`,
    );
    const featuresEnabled = Array.isArray(appSettings)
      ? appSettings.find((entry) => entry.type === 7)
      : null;

    if (featuresEnabled?.settings) {
      user.features_enabled = JSON.parse(featuresEnabled.settings);
    }
  } catch (err) {}

  const { data: permissionData } = await axios.post(
    `${config.api}/user/getpermissions`,
  );
  user.permissions = permissionData;

  try {
    const { data: photoData } = await axios.post(
      `${config.api}/timesheet/getLastPhoto`,
      {
        username_id: user.username_id,
      },
    );

    user.avatar = photoData?.[0]?.photo;
  } catch (err) {}

  return user;
};

const extractApiErrorMessage = (error: any, fallbackMessage: string) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

const getAuthPayload = <T extends Record<string, unknown>>(payload: T): T => {
  // CIG users may not have a platformid that matches the legacy CIG frontend's
  // hardcoded platform value. Omitting the field matches MongerTech's behavior
  // and avoids the server returning a 400 during login.
  if (config.platform === 0) {
    return payload;
  }

  return {
    ...payload,
    platform: config.platform,
  };
};

const AuthContext = createContext<JWTContext>({
  ...initialState,
  platform: "JWT",
  login: () => Promise.resolve(),
  loginWithMicrosoft: () => Promise.resolve(),
  twoFactorLogin: (_token, _user) => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = getStoredAccessToken();

        if (accessToken && verify(accessToken, JWT_SECRET)) {
          setSession(accessToken);
          const user = await hydrateAuthenticatedUser();

          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
              token: accessToken,
            },
          });

          return;
        }
      } catch (err) {}

      setSession(null);
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
          token: null,
        },
      });
    };

    initialize();
  }, []);

  const login = async (
    email: string,
    password: string,
  ): Promise<LoginResult | void> => {
    try {
      const { data } = await axios.post(
        `${config.api}/auth/login/`,
        getAuthPayload({
          username: email,
          password,
        }),
      );

      if (data?.code === 2) {
        return {
          code: data.code,
          user_id: data.user_id,
        };
      }

      const accessToken = data?.token;
      if (!accessToken) {
        throw new Error(data?.message || "Authentication token not returned");
      }

      setSession(accessToken);
      const user = await hydrateAuthenticatedUser();

      dispatch({
        type: ActionType.LOGIN,
        payload: {
          user,
          token: accessToken,
        },
      });
    } catch (error) {
      throw new Error(
        extractApiErrorMessage(
          error,
          "The username or password is incorrect.",
        ),
      );
    }
  };

  const loginWithMicrosoft = async (idToken: string) => {
    try {
      const { data } = await axios.post(
        `${config.api}/auth/microsoft-login/`,
        getAuthPayload({
          id_token: idToken,
        }),
      );

      const accessToken = data?.token;
      if (!accessToken) {
        throw new Error(data?.message || "Authentication token not returned");
      }

      setSession(accessToken);
      const user = await hydrateAuthenticatedUser();

      dispatch({
        type: ActionType.LOGIN,
        payload: {
          user,
          token: accessToken,
        },
      });
    } catch (error) {
      throw new Error(
        extractApiErrorMessage(
          error,
          "Microsoft login failed. Please try again.",
        ),
      );
    }
  };

  const twoFactorLogin = async (
    token: string,
    _user: AuthenticatedUser,
  ) => {
    setSession(token);
    const user = await hydrateAuthenticatedUser();

    dispatch({
      type: ActionType.LOGIN,
      payload: {
        user,
        token,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: ActionType.LOGOUT });
  };

  const register = async (email: string, name: string, password: string) => {
    const response = await axios.post("/api/authentication/register", {
      email,
      name,
      password,
    });

    const accessToken =
      response.data?.accessToken || response.data?.token || null;
    const user = response.data?.user as AuthenticatedUser;

    if (!accessToken || !user) {
      throw new Error("Problem with sign up. Please try again.");
    }

    setSession(accessToken);
    dispatch({
      type: ActionType.REGISTER,
      payload: {
        user,
        token: accessToken,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: "JWT",
        login,
        loginWithMicrosoft,
        twoFactorLogin,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
